import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { HttpStatusCode } from "axios";
import { getApiErrorStatus, getErrorMessageFromUnknown } from "_shared/_errors/api-error-message";
import {
  byIdsResultJSONZ,
  ByIdsResultJSON,
  UnavailableIdJSON,
  UnavailableReason,
} from "_shared/_api/by-ids/by-ids-result";
import { api, getApiEndpointName } from "_shared/_stores/api";
import { logSchemaFailure } from "_shared/_utilities/log-schema-failure";
import Guid from "_shared/guids/guid";
import UpdateEntityAgreementAPIRequest from "legal-entities/agreements/_api/commands/update-entity-agreement-api-command";
import EntityAgreementSchema, { EntityAgreementJSON } from "legal-entities/agreements/_schema/entity-agreement-schema";
import EntityAgreement from "legal-entities/agreements/entity-agreement";
import { AuthResponse } from "users/_api/auth-email-api-service";
import { AuthInfo } from "users/auth/info/auth-info";
import AuthInfoSchema from "users/auth/info/auth-info-schema";
import z from "zod";

export type EntityAgreementPermissionTarget = {
  entityName: string;
  id: string;
};

/** The agreements a get-by-ids read found, and why every other id it was asked about is not among them. */
export type EntityAgreementsByIdsResult = {
  found: EntityAgreement[];
  unavailable: UnavailableIdJSON[];
};

/** What an entity agreement the API did not return says in its place, by the reason it gave (#6122). */
export const unavailableEntityAgreementText: Record<UnavailableReason, string> = {
  missing: "Entity agreement no longer exists",
  unauthorized: "No access to this entity agreement",
};

export type OnboardingWorkTemplate = {
  id: string;
  displayName: string;
  isDefault: boolean;
  participationMode: string;
  workflowVersion: number;
  version: string;
  vendorDisplayName: string;
  name: string;
  description: string;
  effectiveDatePolicy: string;
  endDatePolicy: string;
  negotiable: boolean;
  teamRestricted: boolean;
  conflictsCheckWaived: boolean;
  discount: number;
  feeSchedule: Array<{
    name: string;
    currency: string;
    fixedAmount: number;
  }>;
};

const OnboardingWorkTemplateSchema: z.ZodType<OnboardingWorkTemplate> = z.object({
  id: z.string().min(1).default("legacy-default"),
  displayName: z.string().min(1).default("AttorneyHub onboarding"),
  isDefault: z.boolean().default(false),
  participationMode: z.string().min(1).default("SystemAssisted"),
  workflowVersion: z.number().int().default(1),
  version: z.string().min(1),
  vendorDisplayName: z.string().min(1),
  name: z.string().min(1),
  description: z.string().min(1),
  effectiveDatePolicy: z.string().min(1),
  endDatePolicy: z.string().min(1),
  negotiable: z.boolean(),
  teamRestricted: z.boolean(),
  conflictsCheckWaived: z.boolean(),
  discount: z.number(),
  feeSchedule: z.array(
    z.object({
      name: z.string().min(1),
      currency: z.string().min(1),
      fixedAmount: z.number(),
    }),
  ),
});

export const entityAgreementApi = api.injectEndpoints({
  endpoints: (build) => ({
    getOnboardingWorkTemplate: build.query<OnboardingWorkTemplate, void>({
      query: () => "work/onboarding-template",
      rawResponseSchema: OnboardingWorkTemplateSchema,
      onSchemaFailure: logSchemaFailure,
      transformErrorResponse: (response: FetchBaseQueryError, meta) => toEntityAgreementEndpointError(response, meta),
    }),
    getOnboardingWorkTemplateCatalog: build.query<OnboardingWorkTemplate[], void>({
      query: () => "work/onboarding/templates",
      rawResponseSchema: z.array(OnboardingWorkTemplateSchema),
      onSchemaFailure: logSchemaFailure,
      providesTags: ["OnboardingTemplateCatalog"],
      transformErrorResponse: (response: FetchBaseQueryError, meta) => toEntityAgreementEndpointError(response, meta),
    }),
    getEntityAgreementOnboardingTemplate: build.query<OnboardingWorkTemplate, { id: Guid }>({
      query: ({ id }) => `legal-entities/entity-agreements/${id.value}/onboarding-template`,
      rawResponseSchema: OnboardingWorkTemplateSchema,
      onSchemaFailure: logSchemaFailure,
      transformErrorResponse: (response: FetchBaseQueryError, meta, request) =>
        toEntityAgreementEndpointError(response, meta, { id: request.id }),
    }),
    getEntityAgreementById: build.query<EntityAgreement, { id: Guid }>({
      query: ({ id }) => `legal-entities/entity-agreements/${id.value}`,
      rawResponseSchema: EntityAgreementSchema.EntityAgreementJSONZ,
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: EntityAgreementJSON): EntityAgreement => EntityAgreement.fromJSON(response),
      transformErrorResponse: (response: FetchBaseQueryError, meta, request) =>
        toEntityAgreementEndpointError(response, meta, { id: request.id }),
    }),
    getEntityAgreementsByIds: build.query<EntityAgreementsByIdsResult, { entityAgreementIds: Guid[] }>({
      query: ({ entityAgreementIds }) => ({
        url: "legal-entities/entity-agreements/get-by-ids",
        method: "POST",
        body: entityAgreementIds.map((id) => id.value),
      }),
      rawResponseSchema: byIdsResultJSONZ(EntityAgreementSchema.EntityAgreementJSONZ),
      onSchemaFailure: logSchemaFailure,
      // Every id asked about is answered: an agreement, or an unavailable id saying why it is not one.
      transformResponse: (response: ByIdsResultJSON<EntityAgreementJSON>): EntityAgreementsByIdsResult => ({
        found: EntityAgreement.fromJSONArray(response.found) ?? [],
        unavailable: response.unavailable,
      }),
      transformErrorResponse: (response: FetchBaseQueryError, meta) =>
        toEntityAgreementEndpointError(response, meta),
    }),
    getEntityAgreementPermissionTargetsByIds: build.query<
      ByIdsResultJSON<EntityAgreementPermissionTarget>,
      { entityAgreementIds: Guid[] }
    >({
      query: ({ entityAgreementIds }) => ({
        url: "legal-entities/entity-agreements/get-permission-targets-by-ids",
        method: "POST",
        body: entityAgreementIds.map((id) => id.value),
      }),
      rawResponseSchema: byIdsResultJSONZ(z.object({ entityName: z.string(), id: z.string().uuid() })),
      onSchemaFailure: logSchemaFailure,
    }),
    getEntityAgreementTerms: build.query<Blob, { agreement: EntityAgreement }>({
      query: ({ agreement }) => ({
        url: `legal-entities/agreements/${agreement.id.value}/terms`,
        responseHandler: (response: Response) => response.blob(),
      }),
      transformErrorResponse: (response: FetchBaseQueryError, meta, request) =>
        toEntityAgreementEndpointError(response, meta, { agreement: request.agreement }),
    }),
    updateEntityAgreement: build.mutation<
      EntityAgreement,
      { oldAgreement: EntityAgreement; updatedAgreement: EntityAgreement }
    >({
      query: ({ oldAgreement, updatedAgreement }) => ({
        url: `legal-entities/entity-agreements/${oldAgreement.id.value}`,
        method: "PATCH",
        body: new UpdateEntityAgreementAPIRequest(oldAgreement, updatedAgreement).payload,
      }),
      rawResponseSchema: EntityAgreementSchema.EntityAgreementJSONZ,
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: EntityAgreementJSON): EntityAgreement => EntityAgreement.fromJSON(response),
      transformErrorResponse: (response: FetchBaseQueryError, meta, request) =>
        toEntityAgreementEndpointError(response, meta, { agreement: request.oldAgreement }),
    }),
    acceptEntityAgreement: build.mutation<
      AuthResponse,
      { entityAgreement: EntityAgreement; onboardingWorkTemplateVersion: string; onboardingWorkTemplateId?: string }
    >({
      query: ({ entityAgreement, onboardingWorkTemplateVersion, onboardingWorkTemplateId }) => ({
        url: `legal-entities/entity-agreements/${entityAgreement.id.value}/accept`,
        method: "POST",
        body: {
          onboardingWorkTemplateVersion,
          ...(onboardingWorkTemplateId ?? entityAgreement.onboardingTemplateId
            ? { onboardingWorkTemplateId: onboardingWorkTemplateId ?? entityAgreement.onboardingTemplateId }
            : {}),
        },
      }),
      rawResponseSchema: AuthInfoSchema.AuthInfoJSONZ,
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: unknown): AuthResponse => AuthInfo.parseAuthResponse(response),
      transformErrorResponse: (response: FetchBaseQueryError, meta, request) =>
        toEntityAgreementEndpointError(response, meta, { agreement: request.entityAgreement }),
    }),
  }),
});

export const {
  useGetOnboardingWorkTemplateQuery,
  useGetOnboardingWorkTemplateCatalogQuery,
  useGetEntityAgreementOnboardingTemplateQuery,
  useGetEntityAgreementByIdQuery,
  useGetEntityAgreementsByIdsQuery,
  useGetEntityAgreementPermissionTargetsByIdsQuery,
  useLazyGetEntityAgreementTermsQuery,
  useUpdateEntityAgreementMutation,
  useAcceptEntityAgreementMutation,
} = entityAgreementApi;

export class UnauthorizedEntityAgreementAccessError extends Error {
  constructor(id: Guid) {
    super(`You are not permitted to view this agreement: ${id}`);
  }
}

export class EntityAgreementNotFoundError extends Error {
  public constructor(id: Guid) {
    super(`Unable to find agreement by id: ${id}`);
  }
}

export class InvalidEntityAgreementError extends Error {}

export class InvalidEntityAgreementRequestError extends Error {}

export class EntityAgreementAPIError extends Error {
  public methodName?: string;
  public error: unknown;
  public agreement?: EntityAgreement;

  public constructor(methodName: string | undefined, error: unknown, agreement?: EntityAgreement) {
    const endpointName = methodName ? `.${methodName}` : "";
    super(`Error in entityAgreementApi${endpointName}: ${getEntityAgreementApiErrorMessage(error)}`);
    this.methodName = methodName;
    this.error = error;
    this.agreement = agreement;
  }
}

export function isEntityAgreementRequestCanceled(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const record = error as { name?: unknown; message?: unknown; error?: unknown };
  const name = typeof record.name === "string" ? record.name.toLowerCase() : "";
  const message = typeof record.message === "string" ? record.message.toLowerCase() : "";
  const errorMessage = typeof record.error === "string" ? record.error.toLowerCase() : "";

  return name === "aborterror" || message.includes("aborted") || errorMessage.includes("aborted");
}

export function isOnboardingWorkTemplateChangedError(error: unknown): boolean {
  let current = error;
  const visited = new Set<unknown>();

  while (current && typeof current === "object" && !visited.has(current)) {
    visited.add(current);
    const record = current as { status?: unknown; data?: unknown; error?: unknown };

    if (record.status === HttpStatusCode.Conflict && record.data && typeof record.data === "object") {
      const data = record.data as { code?: unknown; problemCode?: unknown };
      return data.code === "onboarding_work_template_changed" || data.problemCode === "onboarding_work_template_changed";
    }

    current = record.error;
  }

  return false;
}

export function getEntityAgreementApiErrorMessage(error: unknown, fallback = "An error occurred"): string {
  return getErrorMessageFromUnknown(error) ?? fallback;
}

function toEntityAgreementEndpointError(
  response: FetchBaseQueryError,
  meta?: unknown,
  context?: { id?: Guid; agreement?: EntityAgreement },
): Error {
  const status = getApiErrorStatus(response);
  const id = context?.id ?? context?.agreement?.id;
  const methodName = getApiEndpointName(meta);

  if (status === HttpStatusCode.NotFound && id) {
    return new EntityAgreementNotFoundError(id);
  }
  if (status === HttpStatusCode.Forbidden && id) {
    return new UnauthorizedEntityAgreementAccessError(id);
  }
  if (status === HttpStatusCode.BadRequest) {
    return new InvalidEntityAgreementRequestError(
      getErrorMessageFromUnknown(response) ?? "Request failed with status code 400",
    );
  }
  return new EntityAgreementAPIError(methodName, response, context?.agreement);
}
